import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SignIn, Envelope, Lock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Clear any stale/expired token on the login page mount — prevents "invisible" auth failures
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Normalize: trim whitespace + lowercase email — kills the #1 cause of "login not working"
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();

    if (!cleanEmail || !cleanPassword) {
      toast.error('Please enter both email and password');
      setLoading(false);
      return;
    }

    const result = await login(cleanEmail, cleanPassword);

    if (result.success) {
      toast.success('Login successful');
      navigate('/');
    } else {
      toast.error(result.error || 'Invalid email or password');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/4eafec40-04ed-444e-8b0a-e230d9a39680/images/bbb4e0914e5151289c22b136b2ea1ead47b94cf15dc787985290b8966a38481f.png')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-default/90 to-accent-brand/80">
          <div className="h-full flex flex-col justify-center px-12 text-white">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4">
              Reagent Procurement
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-md">
              Streamline your biotech procurement workflow from requisition to purchase order.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex items-center justify-center p-8 bg-surface">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-accent-brand mb-6">
              <SignIn size={32} weight="duotone" className="text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-text-primary">
              Welcome Back
            </h2>
            <p className="mt-2 text-text-secondary">
              Sign in to your account to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-text-primary">
                Email Address
              </Label>
              <div className="relative">
                <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 h-11"
                  placeholder="your.email@company.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-semibold text-text-primary">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-accent-brand hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 h-11"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-accent-brand hover:bg-accent-brand-hover text-accent-brand-foreground font-semibold transition-colors duration-200"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
